import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext.js";
import { cognitoErrorCode, cognitoErrorMessage } from "../lib/auth.js";
import {
  AuthForm,
  AuthShell,
  ErrorBanner,
  SuccessBanner,
  fieldCls,
  labelCls,
  linkCls,
} from "../components/AuthShell.js";
import { Button } from "../components/ui/Button.js";

export function VerifyPage() {
  const { confirmSignUp, resendCode } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [email, setEmail] = useState(params.get("email") ?? "");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await confirmSignUp(email.trim(), code.trim());
      navigate(`/login?email=${encodeURIComponent(email.trim())}`, { replace: true });
    } catch (err) {
      const c = cognitoErrorCode(err);
      if (c === "CodeMismatchException") {
        setError("That code is incorrect.");
      } else if (c === "ExpiredCodeException") {
        setError("That code has expired. Request a new one.");
      } else {
        setError(cognitoErrorMessage(err, "Verification failed."));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleResend() {
    if (!email.trim()) {
      setError("Enter your email first.");
      return;
    }
    setResending(true);
    setError(null);
    setInfo(null);
    try {
      await resendCode(email.trim());
      setInfo("A new code was sent to your email.");
    } catch (err) {
      setError(cognitoErrorMessage(err, "Could not resend code."));
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthShell title="Verify email" subtitle="Enter the 6-digit code Cognito emailed you.">
      <AuthForm onSubmit={handleSubmit}>
        <ErrorBanner message={error} />
        <SuccessBanner message={info} />

        <div>
          <label htmlFor="email" className={labelCls}>
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            className={fieldCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="code" className={labelCls}>
            Verification code
          </label>
          <input
            id="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            className={fieldCls}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>

        <Button type="submit" fullWidth disabled={busy}>
          {busy ? "Verifying…" : "Verify email"}
        </Button>

        <Button
          type="button"
          variant="secondary"
          fullWidth
          disabled={resending}
          onClick={() => void handleResend()}
        >
          {resending ? "Sending…" : "Resend code"}
        </Button>

        <p className="pt-1 text-center text-sm text-muted">
          Ready to sign in?{" "}
          <Link to="/login" className={linkCls}>
            Back to login
          </Link>
        </p>
      </AuthForm>
    </AuthShell>
  );
}
